/**
 * The write seam under apiPost/apiPut/apiPatch/apiDelete.
 *
 * No policy, or signed out: the write goes to the network as today. With a
 * policy, under the entity's lock: if the entity already has queued rows the
 * write joins the queue (a live write must never overtake a queued create);
 * otherwise it is sent, a network failure queues it and asks for a flush, a
 * success is echoed into the cache and seeds the entity's revision. A 409 is
 * handed to the policy's conflict handler after the lock is released; a
 * status the policy accepts (a delete's 404) counts as a success with no reply.
 */

import { broadcastInvalidated } from './cache';
import { localStoreOf, queuedRowsIn } from './echo';
import { withEntityLock } from './entityLock';
import { isNetworkError } from './errors';
import { OutboxUnavailableError, enqueue, refreshCounts } from './outbox';
import { captureScope, scopeIsCurrent, withStores } from './store';
import { writePolicyFor } from './writePolicies';

import type { WriteScope } from './store';
import type { WritePolicy, WriteRequest } from './writePolicies';
import type { ApiError } from '@shared/services/requestTypes';

type Send = () => Promise<unknown>;

let scheduleFlush: (trigger: string) => void = () => undefined;

/** The flusher registers itself so a queued write can kick a replay without a module cycle. */
export function registerFlushScheduler(fn: (trigger: string) => void): void {
  scheduleFlush = fn;
}

/**
 * Echo a successful online write, handing the reply to the policy so the cached
 * copy can carry the new revision. No `rev:<entity>` is written here: nothing
 * is queued for the entity, so there is nothing for a threaded revision to
 * protect, and the cached copy is the source. Never throws.
 */
async function afterOnlineSuccess(
  scope: WriteScope,
  policy: WritePolicy,
  req: WriteRequest,
  data: unknown
): Promise<void> {
  if (!policy.applyLocally) return;
  try {
    await withStores(scope, 'readwrite', async tx =>
      policy.applyLocally!(localStoreOf(tx), req, data)
    );
  } catch {
    // The server has the write; a cache that could not follow is a stale read, never a failed save.
  }
}

/**
 * A direct conflict the handler settled with the server: the cached copies
 * this write touches now describe something else (a create at a suggested
 * path, a rename the retry landed), so they are dropped, inside the write's
 * own scope, and every tab is told the write was acknowledged. A store that
 * will not delete leaves a stale read, never a failed save.
 */
async function forgetTouchedAndAnnounce(
  scope: WriteScope,
  policy: WritePolicy,
  req: WriteRequest
): Promise<void> {
  const keys = policy.touches?.(req) ?? [];
  if (keys.length === 0) return;
  try {
    await withStores(scope, 'readwrite', async tx => {
      const store = localStoreOf(tx);
      for (const key of keys) await store.delete(key);
    });
  } catch {
    // The server has the write; the next read of a key that stayed refetches anyway.
  }
  broadcastInvalidated(keys, 'acknowledged');
}

/** Queue the write and hand back the policy's echo; asks the flusher to try soon. */
async function queueAndEcho<T>(
  scope: WriteScope,
  policy: WritePolicy,
  req: WriteRequest
): Promise<T> {
  await enqueue(scope, req, policy);
  scheduleFlush('enqueue');
  return (policy.echo?.(req) ?? {}) as T;
}

type Locked<T> = { value: T } | { conflict: ApiError };

/**
 * What a failed direct send becomes: a queued write when the network is gone,
 * a success with no reply when the policy accepts the status, a conflict for
 * the handler on a 409, or the error itself.
 */
async function afterSendFailure<T>(
  scope: WriteScope,
  policy: WritePolicy,
  req: WriteRequest,
  err: unknown
): Promise<Locked<T>> {
  if (isNetworkError(err)) return { value: await queueAndEcho<T>(scope, policy, req) };
  const status = (err as ApiError).status;
  if (status !== undefined && policy.acceptStatus?.(status)) {
    await afterOnlineSuccess(scope, policy, req, undefined);
    return { value: (policy.echo?.(req) ?? {}) as T };
  }
  if (status === 409 && policy.onConflict) return { conflict: err as ApiError };
  throw err;
}

/** The part of a write that runs under the entity lock. */
async function writeLocked<T>(
  scope: WriteScope,
  policy: WritePolicy,
  req: WriteRequest,
  entityKey: string,
  send: Send
): Promise<Locked<T>> {
  const queued = await withStores(
    scope,
    'readonly',
    async tx => (await queuedRowsIn(tx, entityKey)).length
  );
  if (queued === null) throw new OutboxUnavailableError();
  if (queued > 0) return { value: await queueAndEcho<T>(scope, policy, req) };
  if (!scopeIsCurrent(scope)) throw new OutboxUnavailableError(); // the session moved while we looked
  let data: unknown;
  try {
    data = await send();
  } catch (err) {
    return afterSendFailure<T>(scope, policy, req, err);
  }
  await afterOnlineSuccess(scope, policy, req, data);
  return { value: data as T };
}

/** Run a write through its policy. */
export async function writeThrough<T>(req: WriteRequest, send: Send): Promise<T> {
  const policy = writePolicyFor(req);
  const scope = captureScope();
  if (!policy || !scope) return (await send()) as T;
  const entityKey = policy.entity(req);
  const locked = await withEntityLock(entityKey, () =>
    writeLocked<T>(scope, policy, req, entityKey, send)
  );
  if ('value' in locked) return locked.value;
  const result = await policy.onConflict!(req, locked.conflict, scope);
  if (result === 'failed') throw locked.conflict;
  // 'resolved-announced': the handler already told the screens; announcing
  // the touched keys as acknowledged here would contradict it.
  if (result === 'resolved') await forgetTouchedAndAnnounce(scope, policy, req);
  await refreshCounts();
  return (policy.echo?.(req) ?? {}) as T;
}
